import {
  Plus,
  LogIn,
  RefreshCw,
  Users,
  Users2,
  Lock,
  ShieldAlert,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getSocket } from '@/lib/socket';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { api, type RoomAccess } from '@/lib/api';
import { prettySequenceName } from '@/lib/utils';
import type { Room, Sequence } from '@/lib/types';
import { useAuth } from '@/features/auth/AuthContext';
import { useRoom } from './useRoom';
import { RoomControl } from './RoomControl';

export function RoomsPage() {
  const roomState = useRoom();
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const loadLists = async () => {
    setRefreshing(true);
    try {
      const [seqs, rms] = await Promise.all([api.listSequences(), api.listRooms()]);
      setSequences(seqs ?? []);
      setRooms(rms ?? []);
    } catch (err) {
      toast.error('Failed to load rooms / sequences', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  // The picker's room list is fetched once on mount, but nothing else
  // refetches it — creating a room, then leaving/ending it and landing
  // back on the picker, previously left the list stale until a manual
  // refresh. Refetch on the "was showing RoomControl, now back on the
  // picker" transition, and on the server's own roomCreated broadcast
  // (mirrors how SequencesPage/ModelsPage stay in sync with each other).
  const wasInRoomRef = useRef(false);
  useEffect(() => {
    const inRoom = !!roomState.room;
    if (wasInRoomRef.current && !inRoom) loadLists();
    wasInRoomRef.current = inRoom;
  }, [roomState.room]);

  useEffect(() => {
    let stop: (() => void) | undefined;
    getSocket().then((s) => {
      const handler = () => loadLists();
      s.on('roomCreated', handler);
      stop = () => s.off('roomCreated', handler);
    });
    return () => stop?.();
  }, []);

  // If we've joined a room, show the control panel and hide the picker
  if (roomState.room) {
    return <RoomControl state={roomState} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Rooms</CardTitle>
            <CardDescription>
              {rooms.length ? `${rooms.length} active` : 'No active rooms'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <CreateRoomForm sequences={sequences} onCreate={roomState.createRoom} />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Refresh"
              onClick={loadLists}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <RoomTable rooms={rooms} onJoin={roomState.joinRoom} onRefresh={loadLists} />
        </CardContent>
      </Card>
    </div>
  );
}

const visibilityOptions: Array<{
  value: RoomAccess['visibility'];
  label: string;
  icon: typeof Users2;
  description: string;
}> = [
  {
    value: 'public',
    label: 'Public',
    icon: Users2,
    description: 'Anyone with the URL or QR can join. Best for customer-facing demos.',
  },
  {
    value: 'authenticated',
    label: 'Signed-in only',
    icon: Lock,
    description: 'Only Cisco team members with a cAspeR account can join.',
  },
  {
    value: 'admin',
    label: 'Admins only',
    icon: ShieldAlert,
    description: 'Only cAspeR admins can join. Use for pre-event setup.',
  },
];

function CreateRoomForm({
  sequences,
  onCreate,
}: {
  sequences: Sequence[];
  onCreate: (name: string, sequenceFile: string, visibility: RoomAccess['visibility']) => void;
}) {
  const { user } = useAuth();
  const canRestrict = user?.role === 'admin' || user?.role === 'editor';

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [sequenceFile, setSequenceFile] = useState<string>('');
  const [visibility, setVisibility] = useState<RoomAccess['visibility']>('public');

  useEffect(() => {
    if (!sequenceFile && sequences[0]) setSequenceFile(sequences[0].file);
  }, [sequences, sequenceFile]);

  const submit = () => {
    if (!name.trim()) return toast.error('Room name is required');
    if (!sequenceFile) return toast.error('Pick a sequence');
    onCreate(name.trim(), sequenceFile, visibility);
    toast.success(`Creating room "${name}"…`, {
      description:
        visibility === 'public' ? undefined : `Visibility: ${visibility}`,
    });
    setOpen(false);
    setName('');
    setVisibility('public');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          New room
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a room</DialogTitle>
          <DialogDescription>
            Rooms group AR clients so they see the same model in lock-step.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="room-name">Room name</Label>
            <Input
              id="room-name"
              autoFocus
              placeholder="e.g. booth-a, demo-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
          </div>
          <div className="grid gap-2">
            <Label>Sequence</Label>
            <Select value={sequenceFile} onValueChange={setSequenceFile}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a sequence…" />
              </SelectTrigger>
              <SelectContent>
                {sequences.map((s) => (
                  <SelectItem key={s.file} value={s.file}>
                    {prettySequenceName(s.file)}{' '}
                    <span className="text-muted-foreground">
                      · {s.data.assets.length} models
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Who can join</Label>
            <Select
              value={visibility}
              onValueChange={(v) => setVisibility(v as RoomAccess['visibility'])}
              disabled={!canRestrict}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {visibilityOptions.map((o) => {
                  const Icon = o.icon;
                  return (
                    <SelectItem key={o.value} value={o.value}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5" />
                        {o.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {visibilityOptions.find((o) => o.value === visibility)?.description}
              {!canRestrict && ' (viewers can only create public rooms)'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button onClick={submit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatCreatedAt(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "2h 14m ago", "45m ago", or "just started" — never negative even if clocks drift. */
function formatRelative(ms: number): string {
  const mins = Math.floor(Math.max(ms, 0) / 60_000);
  if (mins < 1) return 'just started';
  const hours = Math.floor(mins / 60);
  if (hours < 1) return `${mins}m ago`;
  return `${hours}h ${mins % 60}m ago`;
}

type SortKey = 'name' | 'createdAt' | 'userCount';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 10;

function RoomTable({
  rooms,
  onJoin,
  onRefresh,
}: {
  rooms: Room[];
  onJoin: (roomId: string) => void;
  onRefresh: () => void;
}) {
  const { user } = useAuth();
  const isPrivileged = user?.role === 'admin' || user?.role === 'editor';

  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | RoomAccess['visibility']>('all');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [ending, setEnding] = useState(false);

  const canEnd = (r: Room) =>
    isPrivileged ||
    (!!user && !!r.createdByEmail && r.createdByEmail.toLowerCase() === user.email.toLowerCase());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (visibilityFilter !== 'all' && (r.visibility ?? 'public') !== visibilityFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.createdByName ?? '').toLowerCase().includes(q) ||
        (r.createdByEmail ?? '').toLowerCase().includes(q)
      );
    });
  }, [rooms, search, visibilityFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'userCount') cmp = a.userCount - b.userCount;
      else cmp = (a.createdAt ?? 0) - (b.createdAt ?? 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  // Land back on a valid page whenever the filtered set changes shape —
  // otherwise narrowing a search can leave the view stuck on an empty page.
  useEffect(() => {
    setPage(1);
  }, [search, visibilityFilter]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = sorted.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const selectedActionable = [...selected].filter((id) => {
    const r = rooms.find((x) => x.id === id);
    return !!r && canEnd(r);
  });

  const endRooms = async (ids: string[]) => {
    if (ids.length === 0) return;
    const label = ids.length === 1 ? 'this room' : `these ${ids.length} rooms`;
    if (
      !confirm(
        `End ${label}? Every connected client will be disconnected and the link retired permanently. This cannot be undone.`,
      )
    ) {
      return;
    }
    setEnding(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => api.closeRoom(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      const okCount = ids.length - failed;
      if (okCount > 0) toast.success(`Ended ${okCount} session${okCount === 1 ? '' : 's'}`);
      if (failed > 0) toast.error(`Failed to end ${failed} session${failed === 1 ? '' : 's'}`);
      setSelected(new Set());
      onRefresh();
    } finally {
      setEnding(false);
    }
  };

  if (rooms.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        Create a room first, or refresh once someone else has.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {selected.size > 0 ? (
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
          <span className="text-sm">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={ending || selectedActionable.length === 0}
              onClick={() => endRooms(selectedActionable)}
            >
              <X className="h-4 w-4" />
              End {selectedActionable.length} session{selectedActionable.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or creator…"
              className="pl-8"
            />
          </div>
          <Select
            value={visibilityFilter}
            onValueChange={(v) => setVisibilityFilter(v as typeof visibilityFilter)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All visibility</SelectItem>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="authenticated">Signed-in only</SelectItem>
              <SelectItem value="admin">Admins only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
              <th className="w-8 px-3 py-2" />
              <SortableHeader label="Name" sortKey="name" active={sortKey} dir={sortDir} onClick={toggleSort} />
              <th className="px-3 py-2 font-medium">Visibility</th>
              <th className="px-3 py-2 font-medium">Creator</th>
              <SortableHeader label="Created" sortKey="createdAt" active={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableHeader label="Users" sortKey="userCount" active={sortKey} dir={sortDir} onClick={toggleSort} />
              <th className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r) => {
              const end = canEnd(r);
              return (
                <tr key={r.id} className="border-b last:border-0 hover:bg-accent/30">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      disabled={!end}
                      checked={selected.has(r.id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(r.id);
                          else next.delete(r.id);
                          return next;
                        });
                      }}
                      className="h-4 w-4 accent-primary disabled:opacity-30"
                      aria-label={`Select ${r.name}`}
                    />
                  </td>
                  <td className="max-w-[220px] truncate px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2">
                    <VisibilityBadge visibility={r.visibility} />
                  </td>
                  <td className="max-w-[180px] truncate px-3 py-2 text-muted-foreground">
                    {r.createdByName || r.createdByEmail || 'Unknown'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {r.createdAt != null ? (
                      <span title={formatRelative(Date.now() - r.createdAt)}>
                        {formatCreatedAt(r.createdAt)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {r.userCount}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" onClick={() => onJoin(r.id)}>
                        <LogIn className="h-3.5 w-3.5" /> Join
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        disabled={!end || ending}
                        title={end ? 'End this session' : 'Only the creator, editors, or admins can end this session'}
                        onClick={() => endRooms([r.id])}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {sorted.length} room{sorted.length === 1 ? '' : 's'}
          {sorted.length !== rooms.length ? ` (of ${rooms.length})` : ''}
        </span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span>
              Page {pageSafe} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  active,
  dir,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onClick: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <th className="px-3 py-2 font-medium">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        {isActive &&
          (dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function VisibilityBadge({ visibility }: { visibility?: RoomAccess['visibility'] }) {
  const opt =
    visibilityOptions.find((o) => o.value === (visibility ?? 'public')) ?? visibilityOptions[0];
  const Icon = opt.icon;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3 w-3" /> {opt.label}
    </span>
  );
}
